import React, { useCallback, useState } from 'react';
import { FieldLabel } from '@puckeditor/core';

import { isReservedCmsPath, RESERVED_CMS_PATH_ERROR } from '../../standalone-page/reserved-paths';
import { fieldControlStyle } from './field-control-style';

type PathFieldProps = {
  field: { label?: string };
  name: string;
  value: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
};

const PATH_REGEX = /^\/(?:[a-z0-9]+(?:[/\-_][a-z0-9]+)*\/?)?$/;

function validate(path: string): string | null {
  if (!path) return 'Path is required';
  if (!path.startsWith('/')) return 'Must start with /';
  if (/[?#]/.test(path)) return 'Query params and fragments are not allowed';
  if (/:\/\//.test(path)) return 'Must be a relative path, not a full URL';
  if (isReservedCmsPath(path)) return RESERVED_CMS_PATH_ERROR;
  if (!PATH_REGEX.test(path)) return 'Only lowercase letters, numbers, hyphens, underscores, and slashes';
  return null;
}

/** Puck custom field for validated URL paths (e.g. /about, /deals/summer-2026). */
export function PathField({ field, name, value, onChange, readOnly }: PathFieldProps): JSX.Element {
  const [touched, setTouched] = useState(false);
  const error = touched ? validate(value) : null;
  const errorId = `path-error-${name}`;

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.currentTarget.value);
    },
    [onChange]
  );

  const handleBlur = useCallback(() => setTouched(true), []);

  return (
    <FieldLabel label={field.label ?? name} readOnly={readOnly}>
      <input
        type='text'
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={readOnly}
        placeholder='/about-us'
        data-testid={`path-input-${name}`}
        aria-invalid={!!error}
        aria-describedby={error ? errorId : undefined}
        style={{
          ...fieldControlStyle,
          borderColor: error ? '#d32f2f' : fieldControlStyle.borderColor,
        }}
      />
      {error && (
        <span id={errorId} role='alert' style={{ fontSize: 12, color: '#d32f2f', marginTop: 2, display: 'block' }}>
          {error}
        </span>
      )}
    </FieldLabel>
  );
}
